import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Upload, AlertCircle, Check, Clock, X, PenLine, Camera, IdCard } from "lucide-react";

export default function SelgerVerifisering() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [photoFront, setPhotoFront] = useState(null);
  const [photoSide, setPhotoSide] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/seller-verification-status").then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { if (user && user !== false) load(); }, [user?.id]);

  const todayStr = useMemo(() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  }, []);

  const exampleText = user ? `@${user.username} · NorskErotikk.no · ${todayStr}` : "";

  const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-US" };
  const dateLocale = localeMap[i18n.language] || "nb-NO";

  const submit = async () => {
    if (!photoFront || !photoSide) return toast.error(t("seller_verification.upload_both_first"));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo_front", photoFront);
      fd.append("photo_side", photoSide);
      fd.append("note", note);
      await api.post("/me/seller-verification/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("seller_verification.submitted_toast"));
      load();
      setPhotoFront(null); setPhotoSide(null);
      setNote("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!user || user === false) {
    return <Layout><p>{t("seller_verification.login_first")}</p></Layout>;
  }
  if (!status) return <Layout><div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div></Layout>;

  return (
    <Layout withSidebar={false}>
      <div className="max-w-3xl mx-auto" data-testid="seller-verification-page">
        <div className="font-meta mb-3">{t("seller_verification.section_label")}</div>
        <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] mb-4 leading-tight">
          {t("seller_verification.title")}
        </h1>
        <p className="text-[var(--cream-soft)] leading-relaxed mb-6 max-w-2xl">
          {t("seller_verification.intro")}
        </p>

        {/* MONETISERING – chips vs ekte penger + eksterne lenker */}
        <div className="border-l-4 border-[var(--gold)]/60 bg-[var(--gold)]/5 p-5 mb-10 max-w-2xl" data-testid="seller-monetization-info">
          <h3 className="font-fraunces italic text-lg text-[var(--cream)] mb-2">
            {t("seller_verification.monetization_title")}
          </h3>
          <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed mb-3"
             dangerouslySetInnerHTML={{ __html: t("seller_verification.monetization_chips") }} />
          <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed"
             dangerouslySetInnerHTML={{ __html: t("seller_verification.monetization_external") }} />
        </div>

        {/* STATUS-KORT */}
        <div className="border border-white/10 p-6 mb-10" data-testid="seller-status-card">
          {status.seller_verified ? (
            <div className="flex items-start gap-4">
              <ShieldCheck className="h-6 w-6 text-emerald-400 shrink-0 mt-1" />
              <div>
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">{t("seller_verification.verified_title")}</h2>
                <p className="font-manrope text-sm text-[var(--cream-soft)]">
                  {t("seller_verification.verified_text")}
                </p>
              </div>
            </div>
          ) : status.status === "pending" ? (
            <div className="flex items-start gap-4">
              <Clock className="h-6 w-6 text-[var(--gold)] shrink-0 mt-1" />
              <div>
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">{t("seller_verification.pending_title")}</h2>
                <p className="font-manrope text-sm text-[var(--cream-soft)]">
                  {t("seller_verification.pending_text", { date: status.submitted_at ? new Date(status.submitted_at).toLocaleDateString(dateLocale) : "" })}
                </p>
              </div>
            </div>
          ) : status.status === "rejected" ? (
            <div className="flex items-start gap-4">
              <X className="h-6 w-6 text-red-400 shrink-0 mt-1" />
              <div>
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">{t("seller_verification.rejected_title")}</h2>
                {status.reject_reason && (
                  <p className="font-manrope text-sm text-[var(--cream-soft)] mb-2">
                    {t("seller_verification.rejected_reason")} <span className="text-red-400">{status.reject_reason}</span>
                  </p>
                )}
                <p className="font-manrope text-sm text-[var(--cream-soft)]">{t("seller_verification.rejected_can_resend")}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-[var(--cream-faint)] shrink-0 mt-1" />
              <div>
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">{t("seller_verification.not_verified_title")}</h2>
                <p className="font-manrope text-sm text-[var(--cream-soft)]">
                  {t("seller_verification.not_verified_text")}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* SØKNAD-SKJEMA */}
        {!status.seller_verified && status.status !== "pending" && (
          <div data-testid="seller-form" className="space-y-12">

            {/* STEG 1: ALDER */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <IdCard className="h-5 w-5 text-[var(--magenta)]" />
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{t("seller_verification.step1_title")}</h2>
              </div>
              <div className="border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-5">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-[var(--gold)] shrink-0 mt-0.5" />
                  <div>
                    <p
                      className="font-manrope text-sm text-[var(--cream)] leading-relaxed mb-2"
                      dangerouslySetInnerHTML={{ __html: t("seller_verification.step1_main") }}
                    />
                    <p className="font-manrope text-xs text-[var(--cream-soft)] leading-relaxed">
                      {t("seller_verification.step1_note")}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* INSTRUKSJONER */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <PenLine className="h-5 w-5 text-[var(--magenta)]" />
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{t("seller_verification.step2_title")}</h2>
              </div>
              <div className="border-l-4 border-[var(--magenta)]/40 bg-[var(--magenta)]/5 p-5">
                <p
                  className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed mb-3"
                  dangerouslySetInnerHTML={{ __html: t("seller_verification.step2_intro") }}
                />
                <div className="bg-[var(--noir-elev)] border border-[var(--magenta)]/30 p-4 my-4 font-fraunces italic text-xl text-[var(--cream)]" data-testid="seller-example-text">
                  {exampleText}
                </div>
                <ul className="list-disc pl-5 font-manrope text-sm text-[var(--cream-soft)] space-y-1.5 leading-relaxed">
                  <li>{t("seller_verification.step2_rule1")}</li>
                  <li>{t("seller_verification.step2_rule2")}</li>
                  <li>{t("seller_verification.step2_rule3")}</li>
                </ul>
              </div>
            </section>

            {/* BILDE 1 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Camera className="h-5 w-5 text-[var(--magenta)]" />
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{t("seller_verification.step3_title")}</h2>
              </div>
              <div className="space-y-4">
                <ul className="list-decimal pl-5 font-manrope text-sm text-[var(--cream-soft)] space-y-1.5 leading-relaxed">
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step3_rule1") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step3_rule2") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step3_rule3") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step3_rule4") }} />
                </ul>
                <FileField
                  label={t("seller_verification.photo1_label")}
                  hint={t("seller_verification.photo_hint")}
                  selectText={t("seller_verification.file_select")}
                  file={photoFront} onChange={setPhotoFront} testId="seller-photo-front" />
              </div>
            </section>

            {/* BILDE 2 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Camera className="h-5 w-5 text-[var(--magenta)]" />
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{t("seller_verification.step4_title")}</h2>
              </div>
              <div className="space-y-4">
                <ul className="list-decimal pl-5 font-manrope text-sm text-[var(--cream-soft)] space-y-1.5 leading-relaxed">
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step4_rule1") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step4_rule2") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step4_rule3") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("seller_verification.step4_rule4") }} />
                </ul>
                <FileField
                  label={t("seller_verification.photo2_label")}
                  hint={t("seller_verification.photo_hint")}
                  selectText={t("seller_verification.file_select")}
                  file={photoSide} onChange={setPhotoSide} testId="seller-photo-side" />
              </div>
            </section>

            {/* NOTAT */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <PenLine className="h-5 w-5 text-[var(--magenta)]" />
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{t("seller_verification.step5_title")}</h2>
              </div>
              <label className="block font-manrope text-sm text-[var(--cream-soft)] mb-2">
                {t("seller_verification.step5_intro")}
              </label>
              <textarea
                value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={4}
                placeholder={t("seller_verification.step5_placeholder")}
                className="input-editorial w-full" data-testid="seller-note"
              />
            </section>

            {/* SEND-KNAPP */}
            <div className="border-t border-white/10 pt-8">
              <button onClick={submit} disabled={busy || !photoFront || !photoSide}
                className="btn-magenta inline-flex items-center gap-2" data-testid="seller-submit">
                <Upload className="h-4 w-4" />
                {busy ? t("seller_verification.submitting") : t("seller_verification.submit")}
              </button>
              <p className="font-manrope text-sm text-[var(--cream-soft)] mt-4 max-w-xl leading-relaxed">
                {t("seller_verification.submit_note1")}
              </p>
              <p className="font-manrope text-sm text-[var(--cream-soft)] mt-2 max-w-xl leading-relaxed">
                {t("seller_verification.submit_note2_part1")}{" "}
                <Link to="/husregler" className="text-[var(--magenta)] underline-offset-4 hover:underline">{t("seller_verification.submit_note2_link")}</Link>
                {" "}{t("seller_verification.submit_note2_part2")}
              </p>
            </div>

          </div>
        )}
      </div>
    </Layout>
  );
}

function FileField({ label, hint, selectText, file, onChange, testId }) {
  return (
    <div>
      <label className="block font-manrope text-sm text-[var(--cream)] mb-2">{label}</label>
      <label className={`flex items-center gap-4 p-5 border-2 border-dashed cursor-pointer transition ${
        file ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/15 hover:border-white/30"
      }`}>
        {file ? <Check className="h-5 w-5 text-emerald-400 shrink-0" /> : <Upload className="h-5 w-5 text-[var(--cream-soft)] shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-manrope text-sm text-[var(--cream)] truncate">
            {file ? file.name : selectText}
          </div>
          <div className="font-meta text-[var(--cream-faint)] mt-0.5">{hint}</div>
        </div>
        <input type="file" accept="image/*" className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
          data-testid={testId} />
      </label>
    </div>
  );
}
